import { Link } from "react-router-dom";
import {
  Heart,
  Twitter,
  Linkedin,
  ArrowRight,
  ShieldCheck,
  Lock,
  Activity,
  FileCheck,
} from "lucide-react";
import { Wordmark } from "./Brand";
import { SectionPattern } from "./Decor";
import { GeometricBadge } from "./GeometricBadge";
import { FOOTER_SECTIONS, DOCS_URL } from "../../config/navigation";
import { BLOG } from "../../content/blog";
import { GUIDES } from "../../content/guides";
import { COMPARE } from "../../content/compare";
import { marketingLink } from "../../config/origins";

/**
 * MLink — footer links target marketing/content pages. On the app host they
 * cross back to the marketing origin (linklane.io) instead of the noindexed
 * app-host duplicates; on marketing/localhost they stay relative SPA links.
 */
const MLink = ({ to, className, children, ...rest }) => {
  const m = marketingLink(to);
  return m.href ? (
    <a href={m.href} className={className} {...rest}>
      {children}
    </a>
  ) : (
    <Link to={m.to} className={className} {...rest}>
      {children}
    </Link>
  );
};

// Honest, future-ready trust signals (worded as posture, not false claims).
const TRUST = [
  { icon: ShieldCheck, label: "GDPR-ready" },
  { icon: FileCheck, label: "SOC 2-aligned" },
  { icon: Lock, label: "Security", to: "/security" },
  { icon: Activity, label: "Status", to: "/status" },
];

// Content discovery — pulled live from the registry so it scales with content.
function discovery() {
  const out = [];
  if (GUIDES?.[0])
    out.push({ eyebrow: "Latest guide", title: GUIDES[0].title, to: `/guides/${GUIDES[0].slug}` });
  if (COMPARE?.[0])
    out.push({
      eyebrow: "Popular comparison",
      title: COMPARE[0].title,
      to: `/compare/${COMPARE[0].slug}`,
    });
  if (BLOG?.[0])
    out.push({ eyebrow: "From the blog", title: BLOG[0].title, to: `/blog/${BLOG[0].slug}` });
  return out;
}

const FooterLink = ({ link }) =>
  link.href ? (
    <a href={link.href} className="transition-colors hover:text-candy">
      {link.label}
    </a>
  ) : (
    <MLink to={link.to} className="transition-colors hover:text-candy">
      {link.label}
    </MLink>
  );

export const PlayfulFooter = () => {
  const year = new Date().getFullYear();
  const cards = discovery();

  return (
    <footer
      className="relative mt-20 overflow-hidden border-t-2 border-ink bg-yellow-tint"
      data-testid="editorial-footer"
    >
      <SectionPattern kind="dots" opacity={0.35} />

      {/* Content discovery row */}
      {cards.length > 0 && (
        <div className="relative mx-auto max-w-[1400px] px-6 pt-14 md:px-10">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {cards.map((c) => (
              <MLink
                key={c.to}
                to={c.to}
                className="group flex items-center justify-between gap-3 rounded-2xl border-2 border-ink bg-white p-4 shadow-sticker-xs transition-transform duration-200 ease-bouncy hover:-translate-y-0.5"
              >
                <div className="min-w-0">
                  <span className="t-eyebrow text-candy-dark">{c.eyebrow}</span>
                  <p className="mt-1 truncate font-display text-sm font-bold text-ink">{c.title}</p>
                </div>
                <ArrowRight
                  size={16}
                  strokeWidth={3}
                  className="shrink-0 text-ink/40 transition-colors group-hover:text-candy"
                />
              </MLink>
            ))}
          </div>
        </div>
      )}

      {/* Main columns — brand on the left, the 5 link sections in one row */}
      <div className="relative mx-auto max-w-[1400px] px-6 py-14 md:px-10">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.3fr_3.4fr] lg:gap-14">
          <div>
            <Wordmark size="lg" tone="candy" accent="candy" />
            <h2 className="mt-6 font-display text-3xl font-extrabold leading-[0.95] tracking-tight md:text-4xl">
              Every link,
              <br />
              <span className="ai-gradient-text">one platform.</span>
            </h2>
            <div className="mt-6 flex items-center gap-3">
              <a
                href="https://x.com/linklaneio"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkLane on X / Twitter"
                className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-ink bg-white shadow-sticker-xs transition-transform duration-200 ease-bouncy hover:-translate-y-0.5"
              >
                <Twitter size={15} strokeWidth={2} className="text-ink" />
              </a>
              <a
                href="https://www.linkedin.com/company/linklane"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkLane on LinkedIn"
                className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-ink bg-white shadow-sticker-xs transition-transform duration-200 ease-bouncy hover:-translate-y-0.5"
              >
                <Linkedin size={15} strokeWidth={2} className="text-ink" />
              </a>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-x-6 gap-y-8 sm:grid-cols-3 lg:grid-cols-5">
            {FOOTER_SECTIONS.map((section) => (
              <div key={section.heading}>
                <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-muted-foreground">
                  {section.heading}
                </span>
                <ul className="mt-4 space-y-2.5 font-display text-sm font-semibold md:text-base">
                  {section.links.map((link) => (
                    <li key={link.label}>
                      <FooterLink link={link} />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Trust strip */}
      <div className="relative mx-auto max-w-[1400px] px-6 pb-10 md:px-10">
        <div className="flex flex-wrap items-center gap-2.5 rounded-2xl border-2 border-ink bg-white/70 px-5 py-4 shadow-sticker-xs">
          <span className="mr-1 font-mono text-2xs font-semibold uppercase tracking-[0.22em] text-ink/50">
            Trust
          </span>
          {TRUST.map(({ icon: Icon, label, to }) => {
            const badge = (
              <span className="inline-flex items-center gap-1.5 rounded-full border-2 border-ink bg-paper px-3 py-1.5 font-mono text-2xs font-semibold uppercase tracking-[0.18em] text-ink shadow-sticker-xs">
                <Icon size={12} strokeWidth={2.5} className="text-candy" />
                {label}
              </span>
            );
            return to ? (
              <MLink
                key={label}
                to={to}
                className="transition-transform duration-200 ease-bouncy hover:-translate-y-0.5"
              >
                {badge}
              </MLink>
            ) : (
              <span key={label}>{badge}</span>
            );
          })}
        </div>
      </div>

      {/* Giant brand wordmark band — oversized editorial signature across the
          full width, à la modern marketing footers. Decorative (aria-hidden);
          Fraunces serif gives it the premium kiss, "Lane" keeps the candy accent. */}
      <div className="relative overflow-hidden px-4 pt-4" aria-hidden="true">
        <div
          className="select-none text-center font-serif font-medium leading-[0.8] tracking-tight text-ink"
          style={{ fontSize: "clamp(3.5rem, 19vw, 17rem)" }}
        >
          Link<span className="ai-gradient-text">Lane</span>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="relative border-t-2 border-ink bg-paper">
        <div className="mx-auto flex max-w-[1400px] flex-col items-start justify-between gap-2 px-6 py-5 font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground md:flex-row md:items-center md:px-10">
          <span>© {year} LINKLANE — ALL LANES OPEN</span>
          <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <MLink to="/privacy" className="font-extrabold hover:text-candy">
              Privacy
            </MLink>
            <MLink to="/terms" className="font-extrabold hover:text-candy">
              Terms
            </MLink>
            <a href={`${DOCS_URL}/changelog`} className="font-extrabold hover:text-candy">
              Changelog
            </a>
            <span className="flex items-center gap-1.5">
              MADE WITH <Heart size={12} strokeWidth={3} className="text-pink" /> AND DASHED LINES
            </span>
          </span>
        </div>
      </div>
    </footer>
  );
};

export default PlayfulFooter;
